import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import {
  People24Regular,
  ClipboardTaskListLtr24Regular,
  Stack24Regular,
  Box24Regular,
  Home24Regular,
  Navigation24Regular,
} from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useProxyRole } from '../contexts/ProxyRoleContext';
import { canSeeNavPath } from '../config/roleAccess';

interface MobileBottomNavProps {
  onOpenDrawer: () => void;
}

const useStyles = makeStyles({
  bar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow8,
    zIndex: 100,
    paddingBottom: 'env(safe-area-inset-bottom)',
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  tab: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '2px',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    fontWeight: 500,
    minHeight: '56px',
    ...shorthands.padding('6px', '0'),
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  tabActive: {
    color: tokens.colorBrandForeground1,
  },
});

export default function MobileBottomNav({ onOpenDrawer }: MobileBottomNavProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminAuth();
  const { proxyRole } = useProxyRole();

  const effectiveRole = proxyRole ?? user?.role ?? 'office';
  const isDriver = effectiveRole === 'driver';

  // Build up to 4 pinned items based on role permissions. Drivers see Home;
  // everyone else gets the rail's primary group, filtered by the shared
  // canSeeNavPath() (config/roleAccess). Menu button always anchors the 5th
  // slot so the drawer (with the rest) is one tap away.
  const items: { icon: any; path: string; label: string }[] = isDriver
    ? [{ icon: Home24Regular, path: '/', label: 'Home' }]
    : [
        { icon: People24Regular, path: '/customers', label: 'Customers' },
        { icon: ClipboardTaskListLtr24Regular, path: '/orders', label: 'Orders' },
        { icon: Stack24Regular, path: '/projects', label: 'Projects' },
        { icon: Box24Regular, path: '/product_groups', label: 'Inventory' },
      ].filter((item) => canSeeNavPath(effectiveRole, item.path));

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav className={styles.bar} aria-label="Mobile navigation">
      {items.map(item => {
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            className={mergeClasses(styles.tab, isActive(item.path) && styles.tabActive)}
            onClick={() => navigate(item.path)}
            aria-label={item.label}
          >
            <Icon />
            <span>{item.label}</span>
          </button>
        );
      })}
      <button
        className={styles.tab}
        onClick={onOpenDrawer}
        aria-label="Open navigation menu"
      >
        <Navigation24Regular />
        <span>Menu</span>
      </button>
    </nav>
  );
}
